/** The CRM KPI wire shape and its mapper, split out of outreachWire.ts (which re-exports both). */
import type { OutreachFunnelSteps, OutreachKpis, OutreachStageMix } from '@discovr/types';
import { countOrNull, isRecord, numberOrNull } from './outreachWireValues';

/** Go Task 15's per-kind KPI maps — a key Go omits maps to `null` on the FE side. */
export type WireKindRates = { initial?: number | null; followup?: number | null; reply?: number | null };

/** ruling 66's kpis.View verbatim. */
export interface WireKpis {
  reply_rate: number;
  agent_accept_rate: number | null;
  agent_accept_rate_by_kind?: WireKindRates;
  drafts_sent: number;
  drafts_sent_unedited: number;
  /** Absent on Go builds before 2.10 A6. */
  deliverability?: { sent?: number; bounced?: number; complaints?: number } | null;
  generated_at: string;
  /** Seconds the view stays in Go's cache; absent from an older Go. */
  refresh_after_seconds?: number;
  /** CRM v2 (contract §1); every one is absent from an older Go. */
  funnel_steps?: WireFunnelSteps | null;
  creators_in_play?: number | null;
  rates_locked_now?: number | null;
  contact_to_signed?: number | null;
  stage_mix?: WireStageMix | null;
}

export interface WireFunnelSteps {
  candidates?: number;
  reachable?: number;
  contacted?: number;
  replied?: number;
  rate_locked?: number;
  with_brand?: number;
  signed?: number | null;
}

export interface WireStageMix {
  not_contacted?: number;
  in_flight?: number;
  in_conversation?: number;
  locked_signed?: number;
  closed?: number;
  still_moving?: number;
  out_of_play?: number;
  total?: number;
}

const FUNNEL_STEP_KEYS = ['candidates', 'reachable', 'contacted', 'replied', 'rate_locked', 'with_brand'] as const;
const STAGE_MIX_KEYS = ['not_contacted', 'in_flight', 'in_conversation', 'locked_signed', 'closed'] as const;

/** Every named key as a count, or null when any is missing: a partial chart would draw false bars. */
function readCounts<K extends string>(w: Record<string, unknown>, keys: readonly K[]): Record<K, number> | null {
  const counts: Partial<Record<K, number>> = {};
  for (const key of keys) {
    const value = countOrNull(w[key]);
    if (value === null) return null;
    counts[key] = value;
  }
  // Sound: the loop set every key in `keys` or returned early.
  return counts as Record<K, number>;
}

function toFunnelSteps(w: unknown): OutreachFunnelSteps | null {
  if (!isRecord(w)) return null;
  const steps = readCounts(w, FUNNEL_STEP_KEYS);
  if (!steps) return null;
  return {
    candidates: steps.candidates,
    reachable: steps.reachable,
    contacted: steps.contacted,
    replied: steps.replied,
    rateLocked: steps.rate_locked,
    withBrand: steps.with_brand,
    signed: countOrNull(w.signed),
  };
}

function toStageMix(w: unknown): OutreachStageMix | null {
  if (!isRecord(w)) return null;
  const mix = readCounts(w, STAGE_MIX_KEYS);
  if (!mix) return null;
  const stillMoving = mix.not_contacted + mix.in_flight + mix.in_conversation;
  return {
    notContacted: mix.not_contacted,
    inFlight: mix.in_flight,
    inConversation: mix.in_conversation,
    lockedSigned: mix.locked_signed,
    closed: mix.closed,
    stillMoving: countOrNull(w.still_moving) ?? stillMoving,
    outOfPlay: countOrNull(w.out_of_play) ?? mix.closed,
    total: countOrNull(w.total) ?? stillMoving + mix.locked_signed + mix.closed,
  };
}

const MS_PER_SECOND = 1000;

/** Go's seconds as milliseconds, or null when it sent none (0 means uncached, so also no window to keep). */
function refreshAfterMs(seconds: unknown): number | null {
  const s = countOrNull(seconds);
  if (!s) return null;
  return s * MS_PER_SECOND;
}

const byKind = (m: WireKindRates | undefined) => ({ initial: m?.initial ?? null, followup: m?.followup ?? null, reply: m?.reply ?? null });

export function toKpis(w: WireKpis): OutreachKpis {
  return {
    replyRate: w.reply_rate,
    agentAcceptRate: w.agent_accept_rate,
    agentAcceptRateByKind: byKind(w.agent_accept_rate_by_kind),
    draftsSent: w.drafts_sent,
    draftsSentUnedited: w.drafts_sent_unedited,
    deliverability: w.deliverability
      ? {
          sent: w.deliverability.sent ?? 0,
          bounced: w.deliverability.bounced ?? 0,
          complaints: w.deliverability.complaints ?? 0,
        }
      : null,
    generatedAt: w.generated_at,
    funnelSteps: toFunnelSteps(w.funnel_steps),
    creatorsInPlay: countOrNull(w.creators_in_play),
    ratesLockedNow: countOrNull(w.rates_locked_now),
    contactToSigned: numberOrNull(w.contact_to_signed),
    stageMix: toStageMix(w.stage_mix),
    refreshAfterMs: refreshAfterMs(w.refresh_after_seconds),
  };
}
